import { useMemo } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { ICD10Code } from "services/icd10-codes";

type HeaderMostRecentDiagnosisProps = {
  mostRecentIcd10Codes: ICD10Code[];
  /** When false, renders just the codes text (no label prefix) — use inside InfoRow */
  showLabel?: boolean;
  sx?: SxProps;
  typographySx?: SxProps;
};

export default function HeaderMostRecentDiagnosis({
  mostRecentIcd10Codes,
  showLabel = true,
  sx,
  typographySx
}: HeaderMostRecentDiagnosisProps) {
  const hasIcd10Codes = mostRecentIcd10Codes && mostRecentIcd10Codes.length > 0;

  const tooltipContent = useMemo(() => {
    if (!hasIcd10Codes) return undefined;

    return (
      <Stack gap={1}>
        {mostRecentIcd10Codes.map((code) => (
          <Typography key={code.id}>
            <b>{code.code}</b>: {code.description}
          </Typography>
        ))}
      </Stack>
    );
  }, [hasIcd10Codes, mostRecentIcd10Codes]);

  const displayText = useMemo(() => {
    if (!hasIcd10Codes) return showLabel ? "No Recent Diagnosis" : "—";

    const codes = mostRecentIcd10Codes.map(({ code }) => code);
    const codesText =
      codes.length > 3
        ? [...codes.slice(0, 2), `${codes[2]} +${codes.length - 3}`].join(", ")
        : codes.join(", ");

    return showLabel
      ? `Most Recent Diagnosis (ICD-10): ${codesText}`
      : codesText;
  }, [hasIcd10Codes, mostRecentIcd10Codes, showLabel]);

  return (
    <Tooltip title={tooltipContent} disableInteractive placement="top">
      <Box
        sx={{
          width: "max-content",
          fontSize: 12,
          fontWeight: 500,
          background: "rgba(255,255,255,0.85)",
          borderRadius: 1,
          px: 1,
          py: 0.5,
          cursor: "pointer",
          ...sx
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: hasIcd10Codes ? "text.primary" : "text.secondary",
            ...typographySx
          }}
        >
          {displayText}
        </Typography>
      </Box>
    </Tooltip>
  );
}
